// ─── Service ↔ Project Milestone/Outcome assignment (ASSIGNED_TO) ───────────
//
// Nested under a parent `serviceId` with a composite `(serviceId, milestoneId)`
// key, so this does not fit the flat single-id `createEntityCrudClient` shape.

import type { ServiceAssignment } from '../../graphql/types';
import { apiRequest } from './http';

/**
 * Wire-format assignment status accepted by the REST write routes for
 * ASSIGNED_TO assignments (`backend/src/entities/Relationship.ts`
 * `AssignmentStatus` nativeEnum: 'planned' | 'in-progress' | 'done').
 *
 * Distinct on purpose from the GraphQL-facing `AssignmentStatus` enum in
 * graphql/types/project.ts, whose `InProgress` member serializes to the *external*
 * enum name `in_progress` for reads (see backend resolvers.ts enum value
 * config) — sending that value on a REST write fails Zod validation
 * (issue #535).
 */
export type AssignmentStatusInput = 'planned' | 'in-progress' | 'done';

export interface CreateServiceAssignmentInput {
  milestoneId: string;
  outcomeId: string;
  assignmentStatus: AssignmentStatusInput;
  statusNote?: string;
}

export interface UpdateServiceAssignmentInput {
  outcomeId?: string;
  assignmentStatus?: AssignmentStatusInput;
  statusNote?: string;
}

export async function createServiceAssignment(
  serviceId: string,
  input: CreateServiceAssignmentInput
): Promise<ServiceAssignment> {
  return apiRequest<ServiceAssignment>(`/services/${serviceId}/assignments`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateServiceAssignment(
  serviceId: string,
  milestoneId: string,
  input: UpdateServiceAssignmentInput
): Promise<ServiceAssignment> {
  return apiRequest<ServiceAssignment>(`/services/${serviceId}/assignments/${milestoneId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteServiceAssignment(
  serviceId: string,
  milestoneId: string
): Promise<void> {
  return apiRequest<void>(`/services/${serviceId}/assignments/${milestoneId}`, {
    method: 'DELETE',
  });
}
